import { useCallback, useEffect, useState } from "react";
import { MDBSpinner } from "mdb-react-ui-kit";
import "./ExhibitionDetail.css";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import SectionListView from "Projects/EXHI_exhibiciones_project/Components/SectionListView/SectionListView";
import TimelineView from "Projects/EXHI_exhibiciones_project/Components/TimelineView/TimelineView";
import KanbanView from "Projects/EXHI_exhibiciones_project/Components/KanbanView/KanbanView";
import RunsheetView from "Projects/EXHI_exhibiciones_project/Components/RunsheetView/RunsheetView";
import MosaicView from "Projects/EXHI_exhibiciones_project/Components/MosaicView/MosaicView";
import SectionViewer from "Projects/EXHI_exhibiciones_project/Components/SectionViewer/SectionViewer";
import MediaLibraryPanel from "Projects/EXHI_exhibiciones_project/Components/MediaLibraryPanel/MediaLibraryPanel";

const TABS = [
  { id: "list",     label: "Lista" },
  { id: "timeline", label: "Timeline" },
  { id: "kanban",   label: "Kanban" },
  { id: "runsheet", label: "Runsheet" },
  { id: "mosaic",   label: "Mosaico" },
];

function ColHeader({ label, open, onToggle }) {
  return (
    <div className="exhi-col-header" onClick={onToggle}>
      <span>{label}</span>
      <span className={`exhi-col-chevron${open ? "" : " is-closed"}`}>▼</span>
    </div>
  );
}

export default function ExhibitionDetail({ idExhibicion, canManage, onBack, onLoading }) {
  const [exhibicion, setExhibicion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("list");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedSection, setSelectedSection] = useState(null);

  // Collapsible panel states
  const [showInfo,    setShowInfo]    = useState(true);
  const [showLibrary, setShowLibrary] = useState(true);
  const [showViewer,  setShowViewer]  = useState(true);
  const [showNav,     setShowNav]     = useState(true);

  // Carga inicial (o navegación a otra exhibición): muestra spinner
  useEffect(() => { loadExhibicion(false); }, [idExhibicion]); // eslint-disable-line react-hooks/exhaustive-deps
  // Refresh tras DnD / operaciones: silencioso (sin spinner para no blanquear la vista)
  useEffect(() => { if (refreshKey > 0) loadExhibicion(true); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (exhibicion?.secciones?.length > 0) {
      setSelectedSection((prev) => {
        const stillExists = prev && exhibicion.secciones.find((s) => s.idSeccion === prev.idSeccion);
        return stillExists
          ? exhibicion.secciones.find((s) => s.idSeccion === prev.idSeccion)
          : exhibicion.secciones[0];
      });
    } else {
      setSelectedSection(null);
    }
  }, [exhibicion]);

  async function loadExhibicion(silent = false) {
    if (!silent) {
      setLoading(true);
      onLoading && onLoading(true);
    }
    console.log(`[EXHI:Detail] loadExhibicion — id=${idExhibicion} silent=${silent}`);
    try {
      const res = await doFetchJson("EXHI", `exhibiciones/${idExhibicion}`, { method: "GET" });
      console.log(`[EXHI:Detail] loadExhibicion — respuesta: STATE=${res?.STATE} secciones=${res?.DATA?.secciones?.length ?? 0}`);
      if (res?.STATE === "SUCCESS") {
        setExhibicion(res.DATA);
      } else {
        console.warn(`[EXHI:Detail] loadExhibicion — error del backend: ${res?.MESSAGE}`);
        toast.show({ message: res?.MESSAGE || "Error al cargar la exhibición", type: "danger" });
      }
    } catch (err) {
      console.error("[EXHI:Detail] loadExhibicion — ERROR:", err);
      toast.show({ message: "Error de conexión", type: "danger" });
    } finally {
      if (!silent) {
        setLoading(false);
        onLoading && onLoading(false);
      }
    }
  }

  const handleSelectSection = useCallback((s) => setSelectedSection(s), []);

  const handleTimeChange = useCallback((time) => {
    if (!exhibicion?.secciones) return;
    const hit = exhibicion.secciones.find(
      (s) => time >= s.tiempoInicio && time < s.tiempoInicio + s.duracion
    );
    if (hit) setSelectedSection(hit);
  }, [exhibicion]);

  function refresh() { setRefreshKey((k) => k + 1); }

  if (loading) {
    return (
      <div className="exhi-detail exhi-detail-loading">
        <MDBSpinner />
      </div>
    );
  }
  if (!exhibicion) return null;

  const estadoBadgeClass = `exhi-badge-estado exhi-badge-${exhibicion.estado}`;
  const appCode = exhibicion.appCodes?.[0];

  return (
    <div className="exhi-detail">
      <div className="exhi-workspace">

        {/* ── LEFT PANEL ── */}
        <div className="exhi-panel-left">

          {/* Back button */}
          <div className="exhi-left-topbar">
            <button className="exhi-back-btn" onClick={onBack}>← Volver</button>
          </div>

          {/* Exhibition info — collapsible */}
          <div className="exhi-collapsible">
            <ColHeader
              label={exhibicion.titulo}
              open={showInfo}
              onToggle={() => setShowInfo((p) => !p)}
            />
            <div className={`exhi-col-body${showInfo ? "" : " is-collapsed"}`}>
              <div className="exhi-info-block">
                <span className={estadoBadgeClass}>{exhibicion.estado}</span>
                <div className="exhi-info-meta">
                  <span>{exhibicion.secciones?.length || 0} secciones · {exhibicion.creadoPor}</span>
                  {exhibicion.appCodes?.length > 0 && (
                    <span className="text-muted">{exhibicion.appCodes.join(", ")}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Media library — collapsible, grows */}
          <div className="exhi-collapsible exhi-col-grow">
            <ColHeader
              label="Biblioteca de medios"
              open={showLibrary}
              onToggle={() => setShowLibrary((p) => !p)}
            />
            <div className={`exhi-col-body${showLibrary ? "" : " is-collapsed"}`}>
              <MediaLibraryPanel appCode={appCode} exhibicion={exhibicion} />
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="exhi-panel-right">

          {/* Section viewer — collapsible */}
          <div className="exhi-collapsible exhi-viewer-collapsible">
            <ColHeader
              label={selectedSection ? `Visor · ${selectedSection.titulo}` : "Visor"}
              open={showViewer}
              onToggle={() => setShowViewer((p) => !p)}
            />
            <div className={`exhi-col-body${showViewer ? "" : " is-collapsed"}`}>
              <SectionViewer section={selectedSection} />
            </div>
          </div>

          {/* Section nav — collapsible, grows */}
          <div className="exhi-collapsible exhi-col-grow">
            <ColHeader
              label={`Secciones (${exhibicion.secciones?.length || 0})`}
              open={showNav}
              onToggle={() => setShowNav((p) => !p)}
            />
            <div className={`exhi-col-body exhi-col-body-nav${showNav ? "" : " is-collapsed"}`}>
              {/* View selector tabs — above section list */}
              <div className="exhi-nav-tabs">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    className={`exhi-nav-tab-btn${activeTab === t.id ? " active" : ""}`}
                    onClick={() => setActiveTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="exhi-nav-content">
                {activeTab === "list" && (
                  <SectionListView
                    exhibicion={exhibicion}
                    canManage={canManage}
                    onRefresh={refresh}
                    selectedId={selectedSection?.idSeccion}
                    onSelectSection={handleSelectSection}
                  />
                )}
                {activeTab === "timeline" && (
                  <TimelineView
                    exhibicion={exhibicion}
                    canManage={canManage}
                    onRefresh={refresh}
                    externalSelectedId={selectedSection?.idSeccion}
                    onSelectSection={handleSelectSection}
                    onTimeChange={handleTimeChange}
                  />
                )}
                {activeTab === "kanban" && (
                  <KanbanView
                    exhibicion={exhibicion}
                    selectedId={selectedSection?.idSeccion}
                    onSelectSection={handleSelectSection}
                  />
                )}
                {activeTab === "runsheet" && (
                  <RunsheetView exhibicion={exhibicion} />
                )}
                {activeTab === "mosaic" && (
                  <MosaicView
                    exhibicion={exhibicion}
                    selectedId={selectedSection?.idSeccion}
                    onSelectSeccion={handleSelectSection}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
